import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { baseURL } from '../services/api';

const resolveSocketBase = () => {
  const explicitWsBase = (process.env.REACT_APP_WS_BASE_URL || '').trim();
  if (explicitWsBase) {
    return explicitWsBase;
  }

  try {
    if (baseURL) {
      return new URL(baseURL, window.location.origin).origin;
    }
  } catch {
    // fallback below
  }

  return window.location.origin;
};

const buildSocketUrl = (projectId, token) => {
  const socketBase = resolveSocketBase();
  const url = new URL('/ws/repairs/approvals/', socketBase);
  url.protocol = url.protocol.replace('http', 'ws');
  if (projectId) {
    url.searchParams.set('project_id', projectId);
  }
  if (token) {
    url.searchParams.set('token', token);
  }
  return url.toString();
};

const useApprovalsRealtime = (projectId, onMessage) => {
  const { user } = useAuth();
  const socketRef = useRef(null);
  const reconnectTimer = useRef(null);
  const openedRef = useRef(false);

  useEffect(() => {
    const token =
      typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
    if (!user || !token || typeof onMessage !== 'function') return () => {};

    let cancelled = false;
    let reconnectAttempt = 0;

    const start = () => {
      if (cancelled) return;
      const currentToken = localStorage.getItem('access_token');
      if (!currentToken) return;
      const url = buildSocketUrl(projectId, currentToken);
      const ws = new WebSocket(url);
      socketRef.current = ws;
      openedRef.current = false;

      ws.onopen = () => {
        reconnectAttempt = 0;
        openedRef.current = true;
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('useApprovalsRealtime: open', { url });
        }
        try {
          ws.send(JSON.stringify({ action: 'ping' }));
        } catch (_) {}
      };

      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload && payload.type === 'ok' && !payload.action) return;
          if (payload && typeof onMessage === 'function') {
            onMessage(payload);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('useApprovalsRealtime: parse error', err);
        }
      };

      ws.onerror = (event) => {
        if (!openedRef.current) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.debug('useApprovalsRealtime: early error', event);
          }
          return;
        }
        // eslint-disable-next-line no-console
        console.error('useApprovalsRealtime: socket error', event);
      };

      ws.onclose = (evt) => {
        const code = evt?.code;
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('useApprovalsRealtime: close', { code, reason: evt?.reason });
        }
        if (!cancelled && code !== 1000) {
          if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
          reconnectAttempt += 1;
          reconnectTimer.current = setTimeout(() => {
            start();
          }, Math.min(30000, 1000 * (2 ** reconnectAttempt)));
        }
      };
    };

    const delay = setTimeout(start, 40);

    return () => {
      cancelled = true;
      if (delay) clearTimeout(delay);
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      if (socketRef.current) {
        try {
          socketRef.current.close(1000, 'component unmount');
        } catch (_) {}
      }
      socketRef.current = null;
    };
  }, [user, projectId, onMessage]);
};

export default useApprovalsRealtime;
